import { ComponentType, createElement, ReactNode } from "react";
import { LazyRouteFunction, RouteObject } from "react-router";

interface ILazyRouteWithGuardsProps<T extends Record<string, any>> {
  importer: () => Promise<T>;
  componentName: keyof T;
  guards?: ComponentType<{ children: ReactNode }>[];
}

/**
 * Loads a component dynamically using the provided importer function.
 * @param props  Props containing the importer function and component name.
 * @returns
 */
const loadComponent = async <T extends Record<string, any>>(
  props: ILazyRouteWithGuardsProps<T>,
): Promise<ComponentType<{ children?: ReactNode }>> => {
  const module = await props.importer();
  return module[props.componentName];
};

/**
 * Wraps a component with multiple guard components hierarchically in the form of:
 * <Guard1>
 *   <Guard2>
 *     ...
 *      <GuardN>
 *       <Component />
 *     </GuardN>
 *   ...
 *  </Guard2>
 * </Guard1>
 * @param component The main component to be wrapped.
 * @param guards An array of guard components to wrap around the main component.
 * @returns A new component type that represents the main component wrapped with the specified guards.
 */
function wrapComponentWithGuards(
  component: ComponentType<{ children?: ReactNode }>,
  ...guards: ComponentType<{ children: ReactNode }>[]
): ComponentType<{ children?: ReactNode }> {
  return guards.reduceRight<ComponentType<{ children?: ReactNode }>>(
    (guardedComponent, guard) =>
      ({ children }: { children?: ReactNode }) => {
        const guardedElement = createElement(guardedComponent, null, children);
        return createElement(guard, null, guardedElement);
      },
    ({ children }: { children?: ReactNode }) => createElement(component, null, children),
  );
}

/**
 * Type-safe helper to create a lazy-loaded route component with guards.
 * @param routeProps The properties required to create the lazy route with guards.
 * @returns A LazyRouteFunction that loads the component wrapped with the specified guards.
 */
export function lazyRouteWithGuards<T extends Record<string, any>>(
  routeProps: ILazyRouteWithGuardsProps<T>,
): LazyRouteFunction<RouteObject> {
  return async () => {
    const Component = await loadComponent(routeProps);

    const wrappedComponentWithGuards = wrapComponentWithGuards(Component, ...(routeProps.guards ?? []));
    return { Component: wrappedComponentWithGuards };
  };
}

/**
 * Type-safe helper to create a lazy-loaded route.
 * @param routeProps The properties required to create the lazy route.
 * @returns A LazyRouteFunction that loads the component.
 */
export function lazyRoute<T extends Record<string, any>>(
  routeProps: ILazyRouteWithGuardsProps<T>,
): LazyRouteFunction<RouteObject> {
  return async () => {
    const Component = await loadComponent(routeProps);

    return { Component };
  };
}
